import * as PIXI from 'pixi.js';

import { assertString } from '../validators';
import { FONT_STATUS } from '../constants';
import { ErrorReporter } from '../libs/error-reporter';
import type { UpdatableLifecycleAware } from '../types';
import type { AssetNetworkManager } from './AssetNetworkManager';
import { getGlobalAssetNetworkManager } from './AssetNetworkManager';
import { FontLoader } from './FontLoader';

declare global {
    interface Window {
        fontManager: FontManager | null;
    }
}

type FontManagerSettings = {
    assetNetworkManager: AssetNetworkManager;
};

type FontManagerDestroyOptions = {
    keepFallbackFonts?: boolean;
};

/**
 * Retrieves the global font manager.
 * We should load the font manager as a GLOBAL object because we depend on font loading so much we
 * want to re-use as much loaded data as possible and not reload fonts.
 * To use the font loader, simply use window.fontManager
 */
export function getGlobalFontManager(): FontManager {
    // Note we check .classType instead of using instanceof because instanceof doesn't work well
    // across different contexts https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/instanceof#Description
    // and was causing unexpected behaviour in tests
    if (!window.fontManager || window.fontManager.classType !== 'FontManager') {
        // setup fontManager
        const assetNetworkManager = getGlobalAssetNetworkManager();
        window.fontManager = new FontManager({ assetNetworkManager });
    }
    return window.fontManager!;
}

/**
 * Clears the global font manager. If removing fallback fonts then the font manager is also removed,
 * otherwise the fallback fonts are kept and the manager is just cleared of other loaded fonts.
 * Primarily for use between tests to ensure that the global font manager doesn't pollute tests
 */
export function clearGlobalFontManager({ keepFallbackFonts = false } = {}) {
    if (!window.fontManager) {
        return;
    }
    window.fontManager.destroy({ keepFallbackFonts });
    if (!keepFallbackFonts) {
        window.fontManager = null;
    }
}

/**
 * List of fallback urls for fonts. The order of fonts in this list, is the order that
 * the fallback urls will be tried.
 * When updating this list, make sure node rendering follows by updating fallback-fonts.txt
 */
export const FALLBACK_FONT_URLS = [
    // Bulgarian
    // Czech
    // Danish
    // German
    // Greek
    // English
    // Spanish
    // Finnish
    // French
    // Croatian
    // Hungarian
    // Indonesian
    // Italian
    // Macedonian
    // Malay
    // Dutch
    // Norwegian Bokmål
    // Norwegian
    // Polish
    // Portugese
    // Romanian
    // Russian
    // Slovak
    // Serbian
    // Swedish
    // Turkish
    // Ukranian
    // Vietnamese
    'https://storage.googleapis.com/lumen5-site-css/NotoSans-Medium.ttf',
    // Arabic
    'https://storage.googleapis.com/lumen5-site-css/NotoKufiArabic-Medium.ttf',
    'https://storage.googleapis.com/lumen5-site-css/NotoNaskhArabic-Medium.ttf',
    // Hebrew
    'https://storage.googleapis.com/lumen5-site-css/NotoSansHebrew-Medium.ttf',
    // Hindi
    'https://storage.googleapis.com/lumen5-site-css/NotoSansDevanagari-Medium.ttf',
    // Japanese
    'https://storage.googleapis.com/lumen5-site-css/NotoSansJP-Medium.otf',
    // Korean
    'https://storage.googleapis.com/lumen5-site-css/NotoSansKR-Medium.otf',
    'https://storage.googleapis.com/lumen5-site-css/NotoSansKR-Bold.otf', // The Bold font has additional glyphs (row 0000 to 0020) that are not in the Medium font. This was added as a quick workaround to pass some tests. Ideally we should have a better handling for whitespaces. See https://lumen5.atlassian.net/browse/LU-2284
    // Thai
    'https://storage.googleapis.com/lumen5-site-css/NotoSansThai-Medium.ttf',
    // Tagalog
    'https://storage.googleapis.com/lumen5-site-css/NotoSansTagalog-Regular.ttf',
    // Simplified Chinese
    'https://storage.googleapis.com/lumen5-site-css/NotoSansSC-Medium.otf',
    // Traditional Chinese
    'https://storage.googleapis.com/lumen5-site-css/NotoSansTC-Medium.otf',
    // Armenian
    'https://storage.googleapis.com/lumen5-site-css/NotoSansArmenian-Regular.ttf',
    // Georgian
    'https://storage.googleapis.com/lumen5-site-css/DejaVuSans.ttf',
    // Emoji
    'https://storage.googleapis.com/lumen5-site-css/NotoEmoji-Regular.ttf',
    // Gujarati - not well-supported LU-2989
    // 'https://storage.googleapis.com/lumen5-site-css/NotoSansGujarati-Regular.ttf',
    // Bengali - not well-supported see LU-2944 & LU-2945
    // 'https://storage.googleapis.com/lumen5-site-css/NotoSansBengali-Regular.ttf',
];

/**
 * A manager class to load and track Fonts which are used by Luminary. All fonts are loaded using the assetNetworkManager.
 */
export class FontManager extends PIXI.utils.EventEmitter implements UpdatableLifecycleAware {
    classType = 'FontManager';
    autoUpdate = false;
    destroyed = false;
    #fonts: Map<string, FontLoader> = new Map();
    #assetNetworkManager: AssetNetworkManager;

    constructor(settings: FontManagerSettings) {
        super();
        this.#assetNetworkManager = settings.assetNetworkManager;
    }

    /**
     * Returns a list of all of the loaded fonts
     * @returns list of loaded fonts
     */
    get fonts(): string[] {
        return Array.from(this.#fonts.keys());
    }

    /**
     * Returns true if the given sourceUrl is a fallback font
     */
    isFallbackUrl(sourceUrl: string): boolean {
        return FALLBACK_FONT_URLS.indexOf(sourceUrl) !== -1;
    }

    /**
     * Returns the FontLoader for the given sourceUrl if the font has loaded.
     */
    getFontBySourceIfLoaded(sourceUrl: string): FontLoader | null {
        const font = this.#fonts.get(sourceUrl);
        if (font && font.isLoaded) {
            return font;
        }
        return null;
    }

    isFontDoneTryingToLoad(sourceUrl: string): boolean {
        const font = this.#fonts.get(sourceUrl);
        if (!font) {
            return false;
        }
        return font.isDoneTryingToLoad;
    }

    /**
     * Returns the FontLoader for the given sourceUrl if the font exists
     * // TODO make internal to FontManager
     */
    getFontBySource(sourceUrl: string): FontLoader | null {
        if (!this.#fonts.has(sourceUrl)) {
            return null;
        }
        return this.#fonts.get(sourceUrl)!;
    }

    /**
     * Returns a promise for the given sourceUrl which resolves once it is loaded
     */
    async getFontBySourceOnceLoaded(sourceUrl: string): Promise<FontLoader> {
        if (!this.getFontBySource(sourceUrl)) {
            this.addFont(sourceUrl);
        }
        const font = this.getFontBySource(sourceUrl)!;
        if (!font.isLoaded) {
            await new Promise<void>((resolve) => font.once('loaded', () => resolve()));
            return font;
        }
        return font;
    }

    /**
     * Add a new FontLoader from a ttf/otf url if it hasn't been added yet.
     */
    addFont(sourceUrl: string): FontLoader {
        assertString(sourceUrl);
        if (this.#fonts.has(sourceUrl)) {
            return this.#fonts.get(sourceUrl)!;
        }
        const font = new FontLoader({
            parent: this,
            sourceUrl,
        });
        this.#fonts.set(sourceUrl, font);

        this.#assetNetworkManager.waitForNetworkSlot(font, (requestFinished) => {
            font.load()
                .catch((e) => {
                    console.error(`Failed to load font ${sourceUrl}`, e);
                    ErrorReporter.capture(e, {
                        extra: {
                            sourceUrl,
                        },
                    });
                })
                .finally(() => {
                    requestFinished();
                    if (font.status !== FONT_STATUS.FAILED) {
                        this.emit('loaded', sourceUrl);
                        font.emit('loaded');
                    }
                });
        });

        return font;
    }

    /**
     * Removes the internal fonts map.
     */
    destroy({ keepFallbackFonts }: FontManagerDestroyOptions = {}) {
        this.removeAllFonts({ keepFallbackFonts });
        this.removeAllListeners();
        this.destroyed = true;
    }

    removeAllFonts({ keepFallbackFonts }: FontManagerDestroyOptions = {}) {
        this.#fonts.forEach((font, sourceUrl) => {
            if (keepFallbackFonts && this.isFallbackUrl(sourceUrl)) {
                return;
            }
            font.destroy();
            this.#fonts.delete(sourceUrl);
        });
    }

    getFullNameOfLoadedFont(fontUrl: string): string | null {
        const font = this.getFontBySourceIfLoaded(fontUrl);
        if (!font) {
            return null;
        }
        return font.fullName;
    }

    #getLoadedFallbackFontFamilyNames(): string[] {
        return FALLBACK_FONT_URLS.reduce<string[]>((acc, url) => {
            const font = this.getFontBySourceIfLoaded(url);
            if (!font) {
                return acc;
            }
            acc.push(font.familyName);
            return acc;
        }, []);
    }

    #allFallbackFontsFamilyCssString: string | null = null;

    getLoadedFontFamilyNamesCssString(): string {
        if (this.#allFallbackFontsFamilyCssString) {
            return this.#allFallbackFontsFamilyCssString;
        }
        const familyNames = this.#getLoadedFallbackFontFamilyNames();
        const cssString = familyNames.join(', ');
        if (this.#areAllFallbackFontsLoaded) {
            this.#allFallbackFontsFamilyCssString = cssString;
        }
        return cssString;
    }

    async loadAllFallbackFonts() {
        await Promise.all(
            FALLBACK_FONT_URLS.map(async (url) => this.getFontBySourceOnceLoaded(url)),
        );
        this.#areAllFallbackFontsLoaded = true;
        this.emit('loadedAllFallbackFonts');
    }

    #areAllFallbackFontsLoaded: boolean = false;

    async onAllFallbackFontsLoaded(): Promise<void> {
        if (this.areAllFallbackFontsDoneTryingToLoad) {
            return;
        }
        await new Promise<void>((resolve) => {
            this.once('loadedAllFallbackFonts', resolve);
        });
    }

    /**
     * @deprecated Use isLoaded instead.
     */
    get areAllFallbackFontsLoaded(): boolean {
        return this.#areAllFallbackFontsLoaded;
    }

    /**
     * @deprecated Use isDoneTryingToLoad instead.
     */
    get areAllFallbackFontsDoneTryingToLoad(): boolean {
        return (
            this.#areAllFallbackFontsLoaded ||
            FALLBACK_FONT_URLS.every((url) => this.isFontDoneTryingToLoad(url))
        );
    }

    get isLoaded(): boolean {
        return this.areAllFallbackFontsLoaded;
    }

    get isDoneTryingToLoad(): boolean {
        return this.areAllFallbackFontsDoneTryingToLoad;
    }

    get canPlay(): boolean {
        return true;
    }

    _propagatePlay(): void {}
    _propagatePause(): void {}
}
